import { useQueryClient } from 'react-query';
import { getSchemaQueryKey, LimitedSchemaFetchQueryOptions } from '@frontend/react-query-helpers';
import { SchemaSyncConfigV1Service, serviceName } from '../service';
import { GetTrainIO, ServiceQueries } from '../types';
import { useSyncConfigV1Queries } from './use-sync-config-v1-queries';
import { UseSyncConfigV1QueryEndpointArgs, useSyncConfigV1Query } from './use-sync-config-v1-query';

/**
 * A hook that returns a query for the `GetTrain` endpoint.
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetTrainQuery = <E = unknown, D = GetTrainIO['output']>(
  args: UseSyncConfigV1QueryEndpointArgs<'GetTrain', E, D>
) =>
  useSyncConfigV1Query({
    endpointName: 'GetTrain',
    ...args,
  });

/**
 * A hook that returns an array of queries for the `GetTrain` endpoint.
 * It takes an array of objects with the following properties:
 * @param request The request object to pass to the query.
 * @param options (optional) The options to pass to `useQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useGetTrainQueries = <E = unknown, D = GetTrainIO['output']>(
  argsArr: UseSyncConfigV1QueryEndpointArgs<'GetTrain', E, D>[]
) =>
  useSyncConfigV1Queries(
    argsArr.map((args) => ({
      endpointName: 'GetTrain',
      ...args,
    }))
  );

/**
 * A hook that returns a function to fetch the query for the `GetTrain` endpoint.
 * It uses `queryClient.fetchQuery` to ensure proper caching behavior.
 * NOTE: this should only be used in specific scenarios where you need to manually fetch the query. Otherwise,
 * `useQuery` should be used. Manual error and loading states need to be handled when using this function.
 *
 * @returns The function to fetch the query, with the following args:
 * @param req The request object to pass to the query.
 * @param options (optional) The options to pass to `fetchQuery`.
 * @param httpOptions (optional) The http options to pass to the schema function.
 * @param fallbackDataOnError (optional) The fallback data to use when an error occurs. This will not be persisted to the cache.
 * @param requestKeysToOmitFromQueryKey (optional) - An array of keys to omit from the query key. This is useful for cases where the request object contains keys that cause issues
 * when included in the query key, such as the current timestamp.
 */
export const useFetchGetTrain = () => {
  const queryClient = useQueryClient();

  const fetchGetTrain = async <E = unknown, D = GetTrainIO['output']>({
    request,
    options,
    httpOptions,
    requestKeysToOmitFromQueryKey,
    ...rest
  }: Omit<UseSyncConfigV1QueryEndpointArgs<'GetTrain', E, D>, 'options'> & {
    options?: LimitedSchemaFetchQueryOptions<
      GetTrainIO,
      E,
      D,
      ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetTrain'>>
    >;
  }) => {
    const resolvedRequest = typeof request === 'function' ? request({}) : request;
    try {
      const response = await queryClient.fetchQuery<
        GetTrainIO['output'],
        E,
        D,
        ReturnType<typeof getSchemaQueryKey<ServiceQueries, 'GetTrain'>>
      >(
        getSchemaQueryKey<ServiceQueries, 'GetTrain'>({
          serviceName,
          endpointName: 'GetTrain',
          request: resolvedRequest,
          httpOptions,
          requestKeysToOmitFromQueryKey,
        }),
        () => SchemaSyncConfigV1Service.GetTrain(resolvedRequest, httpOptions),
        options
      );

      return response;
    } catch (error) {
      // We check it this way in case the fallbackDataOnError is nullish
      if ('fallbackDataOnError' in rest) {
        return rest.fallbackDataOnError as D;
      }
      throw error;
    }
  };

  return fetchGetTrain;
};
